/**
 * State for importing many signed contracts at once.
 *
 * Each file becomes a row that moves through: queued -> reading -> review ->
 * saving -> saved, and a failed save returns it to review. A row is only
 * saved once it has no blocking problems and either nothing needed checking
 * or someone confirmed it.
 */
import type { RepositoryExtraction } from '../../../services/api/contractService';
import { RepositoryContractFormValues, EMPTY_FORM, validateForm } from './RepositoryContractForm';

export type RowStatus = 'queued' | 'reading' | 'review' | 'saving' | 'saved';

export interface BatchRow {
  id: string;
  file: File;
  status: RowStatus;
  extraction: RepositoryExtraction | null;
  /** Why the document couldn't be read (the row is then filled in by hand). */
  readError: string | null;
  form: RepositoryContractFormValues;
  /** Someone looked at this row and said it's right. */
  confirmed: boolean;
  /** Attach the document to this existing contract instead of creating one. */
  attachTo: { contractId: string; contractTitle: string } | null;
  saveError: string | null;
}

/** Stops a row being saved until it's fixed. */
export type BlockingIssue = 'missing_fields' | 'number_in_use' | 'number_repeated_in_batch';
/** Needs someone to confirm the row before it's saved. */
export type ReviewIssue = 'not_read' | 'not_signed' | 'uncertain_fields' | 'supplier_not_matched';

export interface RowIssues {
  blocking: BlockingIssue[];
  review: ReviewIssue[];
}

export const ISSUE_LABELS: Record<BlockingIssue | ReviewIssue, string> = {
  missing_fields: 'Missing details',
  number_in_use: 'Contract number already in use',
  number_repeated_in_batch: 'Same contract number as another file',
  not_read: "Couldn't be read automatically",
  not_signed: "Doesn't look signed",
  uncertain_fields: 'Some values need checking',
  supplier_not_matched: 'Supplier not matched'
};

export const MAX_BATCH_FILES = 100;

let nextId = 0;
export const createRow = (file: File): BatchRow => ({
  id: `row-${Date.now()}-${nextId++}`,
  file,
  status: 'queued',
  extraction: null,
  readError: null,
  form: EMPTY_FORM,
  confirmed: false,
  attachTo: null,
  saveError: null
});

const normaliseNumber = (value: string) => value.trim().toLowerCase();

export const getRowIssues = (row: BatchRow, rows: BatchRow[]): RowIssues => {
  const blocking: BlockingIssue[] = [];
  const review: ReviewIssue[] = [];

  const extracted = row.extraction?.extracted;
  const looksUnsigned = extracted?.signatureStatus === 'unsigned' || extracted?.signatureStatus === 'partially_signed';

  if (row.attachTo) {
    // Only the document is being added; the contract's details already exist.
    return { blocking, review: looksUnsigned ? ['not_signed'] : [] };
  }

  if (Object.keys(validateForm(row.form)).length > 0) blocking.push('missing_fields');

  const number = normaliseNumber(row.form.contractNumber);
  const duplicate = row.extraction?.duplicate;
  if (number && duplicate?.contractNumber && normaliseNumber(duplicate.contractNumber) === number) {
    blocking.push('number_in_use');
  }
  if (number) {
    // The first file with a number keeps it; later ones in the batch are flagged.
    const first = rows.find(
      (r) => r.status !== 'saved' && !r.attachTo && normaliseNumber(r.form.contractNumber) === number
    );
    if (first && first.id !== row.id) blocking.push('number_repeated_in_batch');
  }

  if (!extracted) review.push('not_read');
  else {
    // "unclear" isn't held up: Word files can never show a handwritten signature.
    if (looksUnsigned) review.push('not_signed');
    if (extracted.uncertainFields.length > 0) review.push('uncertain_fields');
    if (extracted.supplierName && !row.extraction?.supplierMatch) review.push('supplier_not_matched');
  }

  return { blocking, review };
};

export const isRowReady = (row: BatchRow, rows: BatchRow[]): boolean => {
  if (row.status !== 'review') return false;
  const { blocking, review } = getRowIssues(row, rows);
  return blocking.length === 0 && (review.length === 0 || row.confirmed);
};

export const formFromExtraction = (result: RepositoryExtraction): RepositoryContractFormValues => {
  const e = result.extracted;
  return {
    ...EMPTY_FORM,
    supplierId: result.supplierMatch?._id ?? '',
    groupId: result.groupMatch?._id ?? '',
    contractTitle: e?.contractTitle ?? '',
    contractNumber: e?.contractNumber ?? '',
    startDate: e?.startDate ?? '',
    endDate: e?.endDate ?? '',
    // Fall back to the supplier's usual category from the master list.
    category: e?.category ?? result.supplierMatch?.defaultCategory ?? '',
    notes: e?.notes ?? '',
    signatureStatus: e?.signatureStatus ?? '',
    signatureNotes: e?.signatureNotes ?? ''
  };
};

export type BatchAction =
  | { type: 'add'; files: File[] }
  | { type: 'startReading'; id: string }
  | { type: 'read'; id: string; result: RepositoryExtraction }
  | { type: 'readFailed'; id: string; message: string }
  | { type: 'edit'; id: string; form: RepositoryContractFormValues }
  | { type: 'confirm'; id: string }
  | { type: 'attach'; id: string; target: BatchRow['attachTo'] }
  | { type: 'remove'; id: string }
  | { type: 'startSaving'; id: string }
  | { type: 'saved'; id: string }
  | { type: 'saveFailed'; id: string; message: string }
  | { type: 'reset' };

const update = (rows: BatchRow[], id: string, change: (row: BatchRow) => BatchRow) =>
  rows.map((row) => (row.id === id ? change(row) : row));

export const batchReducer = (rows: BatchRow[], action: BatchAction): BatchRow[] => {
  switch (action.type) {
    case 'add': {
      const room = Math.max(0, MAX_BATCH_FILES - rows.length);
      return [...rows, ...action.files.slice(0, room).map(createRow)];
    }
    case 'startReading':
      return update(rows, action.id, (r) => ({ ...r, status: 'reading' }));
    case 'read':
      return update(rows, action.id, (r) => ({
        ...r,
        status: 'review',
        extraction: action.result,
        readError: action.result.extractionError,
        form: formFromExtraction(action.result)
      }));
    case 'readFailed':
      return update(rows, action.id, (r) => ({ ...r, status: 'review', readError: action.message }));
    case 'edit':
      // Editing isn't confirming: the person may not be finished.
      return update(rows, action.id, (r) => ({ ...r, form: action.form, saveError: null }));
    case 'confirm':
      return update(rows, action.id, (r) => ({ ...r, confirmed: true }));
    case 'attach':
      return update(rows, action.id, (r) => ({ ...r, attachTo: action.target, confirmed: false, saveError: null }));
    case 'remove':
      return rows.filter((r) => r.id !== action.id || r.status === 'saving');
    case 'startSaving':
      return update(rows, action.id, (r) => ({ ...r, status: 'saving', saveError: null }));
    case 'saved':
      return update(rows, action.id, (r) => ({ ...r, status: 'saved' }));
    case 'saveFailed':
      // Back to review so it can be fixed and saved again.
      return update(rows, action.id, (r) => ({ ...r, status: 'review', saveError: action.message }));
    case 'reset':
      return [];
    default:
      return rows;
  }
};

export const summarise = (rows: BatchRow[]) => {
  const summary = { total: rows.length, reading: 0, ready: 0, needsReview: 0, saving: 0, saved: 0 };
  for (const row of rows) {
    if (row.status === 'queued' || row.status === 'reading') summary.reading += 1;
    else if (row.status === 'saving') summary.saving += 1;
    else if (row.status === 'saved') summary.saved += 1;
    else if (isRowReady(row, rows)) summary.ready += 1;
    else summary.needsReview += 1;
  }
  return summary;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run a request, waiting and retrying while the server says it's rate
 * limited (HTTP 429). Any other error is thrown straight away.
 */
export const withRateLimitRetry = async <T,>(
  request: () => Promise<T>,
  { attempts = 6, baseDelayMs = 15_000, wait = sleep }: { attempts?: number; baseDelayMs?: number; wait?: (ms: number) => Promise<unknown> } = {}
): Promise<T> => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await request();
    } catch (error) {
      const status = (error as { status?: number })?.status;
      if (status !== 429 || attempt >= attempts) throw error;
      const retryAfter = (error as { retryAfterSeconds?: number }).retryAfterSeconds;
      // Honour Retry-After when given, but never wait more than two minutes at a time.
      await wait(Math.min(retryAfter ? retryAfter * 1000 : baseDelayMs * attempt, 120_000));
    }
  }
};
